import React, { FC, useState, useEffect } from 'react';
import {
  Grid,
  useTheme,
  useMediaQuery,
  Skeleton,
  Autocomplete,
  createFilterOptions,
  Typography,
  Stack,
  Chip,
  TextField,
  Tooltip,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { CorporateFare, WarningAmber } from '@mui/icons-material';
import '../../App.css';
import Card from '../general/Card';
import { capitalize, compareCaseInsensitive, filterDigitsHyphens, formatCurrency, format } from '../../common/utils/common.util';
import { handleError } from '../../common/utils/util.errors';
import { useNavigate } from 'react-router-dom';
import { Box, Button } from 'components/index';
import {
  FileUploadErrorDTO,
  ManualTagsWithRequiredNote,
  ProjectSummary as ProjectSummaryDTO,
  ProjectTagDTO,
  RuleBasedTags,
  TagDTO,
  projectTypeOptions,
  statusOptions,
  monitoringTypeOptions,
} from './project.type';
import { getErrorFieldNames } from './project.util';
import CustomizedTextField from './CustomizedTextField';
import useLookups from '../../hooks/useLookups';
import { City, State } from '../lookups/lookups.type';
import { LOOKUP_KEYS } from '../../common/constants/lookups';
import { getCityString, getStateLabel } from '../lookups/lookups.util';
import useCityStateValidation from '../../hooks/useCityStateValidation';
import NumericFormatInput from '../general/NumericFormatInput';
import CoverImage from '../common/CoverImage/CoverImage';
import { getAllTags, getAllObservers } from './project.api';
import { TAG_CONFIG } from './project.util';
import { UserDTO } from '../user-management/auth.type';

const DEBOUNCE_MS = 500;
interface ProjectSummaryProps {
  project?: ProjectSummaryDTO;
  isLoading: boolean;
  isError: boolean;
  error: any;
  isEditMode: boolean;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  onEditChange: (field: string, value: any) => void;
  editsAllowed: boolean;
  projectErrors: FileUploadErrorDTO[];
  readonly?: boolean;
}

const observerFilterOptions = (options: UserDTO[], state: { inputValue: string }) => {
  const input = state.inputValue.toLowerCase();
  return options.filter(option => `${option.firstName} ${option.lastName}`.toLowerCase().includes(input));
};

const ProjectInfo: FC<ProjectSummaryProps> = ({
  project,
  isLoading,
  isError,
  error,
  isEditMode,
  setIsEditMode,
  onEditChange,
  editsAllowed = false,
  projectErrors,
  readonly,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isScreenBelowMedium = useMediaQuery(theme.breakpoints.down('md'));
  const [postalCode, setPostalCode] = useState('');
  const [cityInputValue, setCityInputValue] = useState('');
  const [citySelectedFromList, setCitySelectedFromList] = useState(false);
  const [selectedState, setSelectedState] = useState('');
  const stateOptions = useLookups(LOOKUP_KEYS.States) as State[];
  const cityOptions = useLookups(LOOKUP_KEYS.Cities) as City[];
  const cityOptionStrings = cityOptions.map(getCityString);
  const { status: projectCityStateStatus, checkOnBlur: checkProjectCityStateOnBlur } = useCityStateValidation(
    cityOptions,
    citySelectedFromList ? null : cityInputValue,
    selectedState,
  );
  const isProjectCityStateInvalid = !citySelectedFromList && projectCityStateStatus === 'invalid';
  const isProjectCityStateChecking = !citySelectedFromList && projectCityStateStatus === 'checking';
  const errorFieldNames = [...getErrorFieldNames(projectErrors), ...(isProjectCityStateInvalid ? ['City', 'State'] : [])];
  const [availableTags, setAvailableTags] = useState<TagDTO[]>([]);
  const [selectedTags, setSelectedTags] = useState<ProjectTagDTO[]>([]);
  const [availableObservers, setAvailableObservers] = useState<UserDTO[]>([]);
  const [tagNoteDialogOpen, setTagNoteDialogOpen] = useState(false);
  const [tagNote, setTagNote] = useState('');
  const [pendingTags, setPendingTags] = useState<TagDTO[]>([]);
  const [pendingTagRequiringNote, setPendingTagRequiringNote] = useState<TagDTO | null>(null);
  // Local drafts so keystrokes update these numeric fields instantly without pushing into the
  // shared project state on every keystroke. Only committed via onEditChange on blur. Seeded
  // from and re-synced with the actual project value (not undefined), so blurring a field
  // without typing in it commits the unchanged value instead of clearing it.
  const [grossBuildingAreaDraft, setGrossBuildingAreaDraft] = useState(project?.grossBuildingArea);
  const [netAreaDraft, setNetAreaDraft] = useState(project?.netArea);
  useEffect(() => {
    setGrossBuildingAreaDraft(project?.grossBuildingArea);
    setNetAreaDraft(project?.netArea);
  }, [project?.grossBuildingArea, project?.netArea]);

  // Fetch all available tags when component loads
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const tags = await getAllTags();
        setAvailableTags(tags);
      } catch (error) {
        console.error('Failed to fetch tags:', error);
      }
    };

    fetchTags();
  }, []);

  // Fetch all available observers when component loads
  useEffect(() => {
    const fetchObservers = async () => {
      try {
        const observers = await getAllObservers();
        setAvailableObservers(observers);
      } catch (error) {
        console.error('Failed to fetch observers:', error);
      }
    };

    fetchObservers();
  }, []);

  // Set selected tags when project changes
  useEffect(() => {
    if (project?.tags) {
      setSelectedTags(project.tags);
    }
  }, [project?.tags]);

  useEffect(() => {
    if (project?.projectAddress?.postalCode) {
      setPostalCode(project?.projectAddress?.postalCode);
    }
    if (project?.projectAddress?.city) {
      setCityInputValue(capitalize(project.projectAddress.city) ?? '');
    }
    if (project?.projectAddress?.state) {
      let matchingState = stateOptions.find(option => compareCaseInsensitive(option.stateCode, project?.projectAddress?.state ?? ''));
      if (!matchingState) {
        matchingState = stateOptions.find(option => compareCaseInsensitive(option.name, project?.projectAddress?.state ?? ''));
      }
      setSelectedState(matchingState ? (capitalize(matchingState.name) ?? '') : project.projectAddress.state);
    }
  }, [project, stateOptions]);

  const handleTagsChange = (event: any, newValue: TagDTO[]) => {
    const newlyAddedTagRequiringNote = newValue.find(
      tag => ManualTagsWithRequiredNote.includes(tag.name as any) && !selectedTags.some(st => st.tag.name === tag.name),
    );

    if (newlyAddedTagRequiringNote) {
      // A tag requiring a reason was just added — open dialog to collect it
      setPendingTags(newValue);
      setPendingTagRequiringNote(newlyAddedTagRequiringNote);
      setTagNote('');
      setTagNoteDialogOpen(true);
      return;
    }

    const userSelectedTags: ProjectTagDTO[] = newValue.map((tag: TagDTO) => ({
      id: tag.id,
      tag: tag,
      details: ManualTagsWithRequiredNote.includes(tag.name as any)
        ? selectedTags.find(st => st.tag.name === tag.name)?.details
        : undefined,
    }));

    onEditChange('tags', userSelectedTags);
    setSelectedTags(userSelectedTags);
  };

  const handleTagNoteConfirm = () => {
    const userSelectedTags: ProjectTagDTO[] = pendingTags.map((tag: TagDTO) => ({
      id: tag.id,
      tag: tag,
      details:
        tag.name === pendingTagRequiringNote?.name
          ? tagNote.trim()
          : ManualTagsWithRequiredNote.includes(tag.name as any)
            ? selectedTags.find(st => st.tag.name === tag.name)?.details
            : undefined,
    }));

    onEditChange('tags', userSelectedTags);
    setSelectedTags(userSelectedTags);
    setTagNoteDialogOpen(false);
    setPendingTags([]);
    setPendingTagRequiringNote(null);
    setTagNote('');
  };

  const handleTagNoteCancel = () => {
    setTagNoteDialogOpen(false);
    setPendingTags([]);
    setPendingTagRequiringNote(null);
    setTagNote('');
  };

  if (isError) {
    handleError(error);
    navigate('./NotFound'); // Use the navigate function to go to the 404 page
    return null;
  }
  if (isLoading)
    return (
      <Grid container spacing={2} mb={2}>
        <Grid size={{ xs: 12 }}>
          <Skeleton variant='rounded' width={'100%'} height={120} />
        </Grid>
      </Grid>
    );
  if (!project) return null;

  return (
    <>
      <Box mb={2}>
        {/* <Typography variant='h5' color='primary' ml={2} mb={3} align='left'>
        Project Summary
      </Typography> */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 8 }}>
            <Card
              icon={<CorporateFare color='primary' />}
              heading={
                <Stack direction={'row'} alignItems={'center'} spacing={1} flexWrap='wrap'>
                  <Typography variant='h5' color='primary'>
                    Project Info
                  </Typography>
                  {project.status && <Chip label={project.status} size='small' variant='outlined' color='info' sx={{ ml: 1 }} />}
                  {project.corporation?.aiCaution && (
                    <Tooltip
                      title={`This project's corporation (${project.corporation.name}) is flagged for AI Caution`}
                      placement='top'
                      arrow
                    >
                      <Chip icon={<WarningAmber />} label='AI Caution' size='small' variant='outlined' color='warning' />
                    </Tooltip>
                  )}
                </Stack>
              }
              data={{
                'Project Title': isEditMode ? (
                  <CustomizedTextField
                    placeholder='e.g., The Howard Apartments'
                    defaultValue={project?.projectTitle}
                    onBlur={e => onEditChange('projectTitle', e.target.value)}
                    type='text'
                    hasError={errorFieldNames.includes('Loan Officer Name')}
                  />
                ) : (
                  project?.projectTitle || '--'
                ),
                'Project Number': isEditMode ? (
                  <CustomizedTextField
                    placeholder='C12345'
                    defaultValue={project?.projectNumber}
                    onBlur={e => onEditChange('projectNumber', e.target.value)}
                    type='text'
                    hasError={errorFieldNames.includes('Project Number')}
                  />
                ) : (
                  project?.projectNumber || '--'
                ),
                'Project Type': isEditMode ? (
                  <Autocomplete
                    options={projectTypeOptions}
                    getOptionLabel={option => option.label}
                    defaultValue={projectTypeOptions.find(option => option.label === project?.projectType)}
                    onChange={(event, newValue) => onEditChange('projectType', newValue ? newValue.label : '')}
                    renderInput={params => (
                      <CustomizedTextField placeholder='Select an option' {...params} hasError={errorFieldNames.includes('Project Type')} />
                    )}
                  />
                ) : (
                  (project?.projectType ?? '--')
                ),
                'Gross Building Area (SF)': isEditMode ? (
                  <CustomizedTextField
                    placeholder='0'
                    value={grossBuildingAreaDraft}
                    onChange={e => setGrossBuildingAreaDraft(e.target.value)}
                    onBlur={() => onEditChange('grossBuildingArea', grossBuildingAreaDraft)}
                    InputProps={{
                      inputComponent: NumericFormatInput as any,
                    }}
                    hasError={errorFieldNames.includes('Gross Building Area (SF)')}
                  />
                ) : (
                  format(project?.grossBuildingArea ?? '--', 'number')
                ),
                'Net Area (SF)': isEditMode ? (
                  <CustomizedTextField
                    placeholder='0'
                    value={netAreaDraft}
                    onChange={e => setNetAreaDraft(e.target.value)}
                    onBlur={() => onEditChange('netArea', netAreaDraft)}
                    InputProps={{
                      inputComponent: NumericFormatInput as any,
                    }}
                    hasError={errorFieldNames.includes('Net Area (SF)')}
                  />
                ) : (
                  format(project?.netArea ?? '--', 'number')
                ),
                Budget: formatCurrency(project.budget?.toString() ?? '--'),
                'Monitoring Type': isEditMode ? (
                  <Autocomplete
                    options={monitoringTypeOptions}
                    getOptionLabel={option => (typeof option === 'string' ? option : option.label)}
                    value={project?.monitoringType ? { label: project.monitoringType, value: project.monitoringType } : null}
                    onChange={(event, newValue) => onEditChange('monitoringType', newValue ? newValue.label : '')}
                    renderInput={params => (
                      <CustomizedTextField
                        placeholder='Select an option'
                        {...params}
                        hasError={errorFieldNames.includes('Monitoring Type')}
                      />
                    )}
                  />
                ) : project?.monitoringType ? (
                  <Stack direction='row' spacing={1} flexWrap='wrap' py={0.5} gap={0.5}>
                    <Chip
                      label={project.monitoringType}
                      size='small'
                      variant='outlined'
                      sx={{
                        fontWeight: 500,
                        margin: '0 !important',
                        display: 'inline-flex',
                        alignSelf: 'flex-start',
                        width: 'fit-content',
                        maxWidth: '100%',
                        borderColor: '#0283a0',
                        color: '#0283a0',
                        '& .MuiChip-label': {
                          px: 1,
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  </Stack>
                ) : (
                  '--'
                ),
                Status: isEditMode ? (
                  <Autocomplete
                    options={statusOptions}
                    getOptionLabel={option => option.label}
                    defaultValue={statusOptions.find(option => option.label === project?.status)}
                    onChange={(event, newValue) => onEditChange('status', newValue ? newValue.label : '')}
                    renderInput={params => (
                      <CustomizedTextField placeholder='Select an option' {...params} hasError={errorFieldNames.includes('Status')} />
                    )}
                  />
                ) : (
                  project?.status || '--'
                ),
                'Project Address': isEditMode ? (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <CustomizedTextField
                        placeholder='Street'
                        defaultValue={project?.projectAddress?.street}
                        onBlur={e => onEditChange('projectAddress.street', e.target.value)}
                        type='text'
                        fullWidth
                        hasError={errorFieldNames.includes('Project Address')}
                      />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Autocomplete
                        freeSolo
                        options={cityOptionStrings}
                        getOptionLabel={option => option}
                        inputValue={cityInputValue}
                        onInputChange={(_, value, reason) => {
                          if (reason === 'input') {
                            setCityInputValue(value);
                            setCitySelectedFromList(false);
                          }
                        }}
                        onChange={(_, newValue) => {
                          const isFromList = newValue && typeof newValue === 'string' && cityOptionStrings.includes(newValue);
                          if (isFromList && newValue) {
                            const parts = newValue.split(', ');
                            const cityName = parts[0] ?? '';
                            const stateCode = parts[1] ?? '';
                            setCityInputValue(cityName);
                            onEditChange('projectAddress.city', cityName);
                            const matchingState = stateOptions.find(option => compareCaseInsensitive(option.stateCode, stateCode));
                            const stateName = capitalize(matchingState?.name) ?? '';
                            setSelectedState(stateName);
                            onEditChange('projectAddress.state', stateName);
                            setCitySelectedFromList(true);
                          } else {
                            setCitySelectedFromList(false);
                            if (newValue !== null) {
                              const val = typeof newValue === 'string' ? newValue : '';
                              setCityInputValue(val);
                              onEditChange('projectAddress.city', val);
                            }
                          }
                        }}
                        filterOptions={createFilterOptions({ limit: 10, matchFrom: 'start' })}
                        renderInput={params => (
                          <CustomizedTextField
                            {...params}
                            placeholder='City'
                            label='City'
                            sx={{
                              '& .MuiInputBase-input': {
                                height: '10px',
                                fontSize: '0.875rem',
                                padding: '0px 0px',
                              },
                            }}
                            fullWidth
                            hasError={errorFieldNames.includes('City')}
                            onBlur={() => {
                              onEditChange('projectAddress.city', cityInputValue);
                              checkProjectCityStateOnBlur();
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      {citySelectedFromList ? (
                        <CustomizedTextField
                          value={selectedState}
                          placeholder='State'
                          label='State'
                          type='text'
                          fullWidth
                          inputProps={{ readOnly: true }}
                          sx={{
                            pointerEvents: 'none',
                            '& .MuiInputBase-input': {
                              height: '10px',
                              fontSize: '0.875rem',
                              padding: '0px 0px',
                            },
                          }}
                          hasError={errorFieldNames.includes('State')}
                        />
                      ) : (
                        <Autocomplete
                          freeSolo
                          options={stateOptions}
                          getOptionLabel={option => (typeof option === 'string' ? option : (getStateLabel(option) ?? ''))}
                          value={selectedState}
                          inputValue={selectedState}
                          onInputChange={(_, newInputValue, reason) => {
                            if (reason === 'input') {
                              setSelectedState(newInputValue);
                            }
                          }}
                          onChange={(_, newValue) => {
                            const stateName = typeof newValue === 'string' ? newValue : newValue ? (getStateLabel(newValue) ?? '') : '';
                            setSelectedState(stateName);
                            onEditChange('projectAddress.state', stateName);
                          }}
                          filterOptions={createFilterOptions({ limit: 10, matchFrom: 'start' })}
                          renderInput={params => (
                            <CustomizedTextField
                              {...params}
                              placeholder='State'
                              label='State'
                              fullWidth
                              hasError={errorFieldNames.includes('State')}
                              onBlur={() => {
                                onEditChange('projectAddress.state', selectedState);
                                checkProjectCityStateOnBlur();
                              }}
                              helperText={
                                isProjectCityStateInvalid
                                  ? 'This city/state combination does not exist'
                                  : isProjectCityStateChecking
                                    ? 'Verifying city/state…'
                                    : ''
                              }
                              sx={{
                                '& .MuiInputBase-input': {
                                  height: '10px',
                                  fontSize: '0.875rem',
                                  padding: '0px 0px',
                                },
                              }}
                            />
                          )}
                        />
                      )}
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <CustomizedTextField
                        value={postalCode}
                        placeholder='Postal Code'
                        label='Postal Code'
                        onChange={e => setPostalCode(filterDigitsHyphens(e.target.value))}
                        onBlur={() => onEditChange('projectAddress.postalCode', postalCode)}
                        type='text'
                        fullWidth
                        hasError={errorFieldNames.includes('Postal Code')}
                      />
                    </Grid>
                  </Grid>
                ) : project?.projectAddress ? (
                  (project?.projectAddress?.city === project?.projectAddress?.postalCode ||
                    project?.projectAddress?.city === project?.projectAddress?.street) &&
                  project?.projectAddress?.city != '' ? (
                    project?.projectAddress?.city
                  ) : (
                    [
                      project?.projectAddress?.street,
                      capitalize(project?.projectAddress?.city),
                      project?.projectAddress?.state?.toUpperCase(),
                      project?.projectAddress?.postalCode,
                    ]
                      .filter(Boolean)
                      .join(', ')
                  )
                ) : (
                  '--'
                ),
                Observer: isEditMode ? (
                  <Autocomplete
                    options={availableObservers}
                    getOptionLabel={option => `${option.firstName} ${option.lastName}`}
                    filterOptions={observerFilterOptions}
                    value={availableObservers.find(observer => observer.id === project?.observer?.id) || null}
                    onChange={(event, newValue) => {
                      onEditChange('observer', newValue);
                    }}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <ListItemText primary={`${option.firstName} ${option.lastName}`} />
                      </li>
                    )}
                    renderInput={params => (
                      <CustomizedTextField {...params} placeholder='Select an observer' hasError={errorFieldNames.includes('Observer')} />
                    )}
                  />
                ) : project?.observer ? (
                  `${project.observer.firstName} ${project.observer.lastName}`
                ) : (
                  '--'
                ),
                Tags: isEditMode ? (
                  <Autocomplete
                    multiple
                    id='project-tags'
                    options={availableTags.filter(tag => !RuleBasedTags.includes(tag.name as any))}
                    getOptionLabel={option => option.name}
                    value={availableTags.filter(tag => selectedTags.some(st => st.tag.id === tag.id))}
                    getOptionDisabled={option => RuleBasedTags.includes(option.name as any)}
                    onChange={handleTagsChange}
                    renderOption={(props, option) => {
                      return (
                        <li {...props}>
                          <Chip
                            label={option.name}
                            key={option.name}
                            variant='outlined'
                            sx={{
                              color: `${TAG_CONFIG[option.name]?.color || 'default'} !important`,
                              backgroundColor: `${TAG_CONFIG[option.name]?.backgroundColor || 'default'} !important`,
                              borderColor: `${TAG_CONFIG[option.name]?.backgroundColor || 'default'} !important`,
                              fontWeight: 500,
                              pointerEvents: 'none',
                            }}
                          />
                        </li>
                      );
                    }}
                    renderInput={params => <TextField {...params} placeholder='Select tags' />}
                    renderTags={(tagValues, getTagProps) =>
                      tagValues.map((option, index) => {
                        const isAutomaticTag = RuleBasedTags.includes(option.name as any);
                        return (
                          <Chip
                            label={option.name}
                            {...getTagProps({ index })}
                            onDelete={isAutomaticTag ? undefined : getTagProps({ index }).onDelete}
                            key={option.name}
                            variant='outlined'
                            sx={{
                              color: `${TAG_CONFIG[option.name]?.color || 'default'} !important`,
                              backgroundColor: `${TAG_CONFIG[option.name]?.backgroundColor || 'default'} !important`,
                              borderColor: `${TAG_CONFIG[option.name]?.backgroundColor || 'default'} !important`,
                              fontWeight: 500,
                              pointerEvents: isAutomaticTag ? 'none' : 'auto',
                            }}
                          />
                        );
                      })
                    }
                  />
                ) : (
                  <Stack direction='row' spacing={1} flexWrap='wrap' py={0.5} gap={0.5}>
                    {project.tags?.map(tag => (
                      <Tooltip
                        key={tag.id}
                        title={
                          TAG_CONFIG[tag.tag?.name]?.getTooltipContent(project) && (
                            <div style={{ maxWidth: '700px', paddingInline: '0.5rem', textAlign: 'left' }}>
                              {TAG_CONFIG[tag.tag?.name]?.getTooltipContent(project)}
                            </div>
                          )
                        }
                      >
                        <Chip
                          label={tag.tag?.name}
                          size='small'
                          variant='outlined'
                          sx={{
                            color: TAG_CONFIG[tag.tag?.name]?.color || 'default',
                            backgroundColor: TAG_CONFIG[tag.tag?.name]?.backgroundColor || 'default',
                            borderColor: TAG_CONFIG[tag.tag?.name]?.backgroundColor || 'default',
                            fontWeight: 500,
                            margin: '0 !important',
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Stack>
                ),
              }}
              itemsPerRow={isEditMode ? (isScreenBelowMedium ? 1 : 2) : isScreenBelowMedium ? 2 : 3}
              isEditMode={isEditMode}
              showEditIcon={editsAllowed}
              onEditClick={() => setIsEditMode(true)}
              errorFields={errorFieldNames}
              disableActions={readonly}
            />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <CoverImage project={project} />
          </Grid>
        </Grid>
      </Box>

      <Dialog open={tagNoteDialogOpen} onClose={handleTagNoteCancel} maxWidth='sm' fullWidth disableRestoreFocus>
        <DialogTitle>Mark as {pendingTagRequiringNote?.name}</DialogTitle>
        <DialogContent>
          <Typography variant='body2' mb={1.5}>
            Please provide a reason for marking this project as &ldquo;{pendingTagRequiringNote?.name}&rdquo;.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            placeholder='Enter reason...'
            value={tagNote}
            onChange={e => setTagNote(e.target.value)}
            error={tagNote.trim() === ''}
            helperText={tagNote.trim() === '' ? 'A reason is required' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={handleTagNoteCancel}>
            Cancel
          </Button>
          <Button onClick={handleTagNoteConfirm} disabled={!tagNote.trim()}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProjectInfo;
